import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Grid, Typography, CircularProgress, Paper, Box, Button, Chip, IconButton, Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { toast } from 'react-hot-toast';
import { api } from '../../services/api';
import { STAGE_LABELS } from '../../utils/labels';
import { exportHistoryToExcel } from '../../utils/exportHistory';

const DEVICE_ID = 'esp32_001';

export default function PlantHistory() {
    const navigate = useNavigate();
    const [plants, setPlants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const load = () => {
        setLoading(true);
        api.get(`/plants/history?deviceId=${DEVICE_ID}`).then(res => { setPlants(res.data); setLoading(false); })
            .catch(() => setLoading(false));
    };

    useEffect(load, []);

    const handleDelete = async (id) => {
        if (!window.confirm('Remover esta planta do histórico?')) return;
        try {
            await api.delete(`/plants/${id}`);
            toast.success('Planta removida do histórico');
            load();
        } catch (e) { toast.error('Erro ao remover'); }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            // Busca plantas ativas + stats para compor o relatório completo
            const [activeRes, statsRes] = await Promise.all([
                api.get(`/plants?deviceId=${DEVICE_ID}`).catch(() => ({ data: [] })),
                api.get(`/stats?deviceId=${DEVICE_ID}`).catch(() => ({ data: null }))
            ]);
            const allPlants = [...(activeRes.data || []), ...plants];
            const fileName = exportHistoryToExcel({
                plants: allPlants,
                stats: statsRes.data?.stats || null,
                deviceId: DEVICE_ID
            });
            toast.success(`Arquivo exportado: ${fileName}`);
        } catch (e) {
            console.error(e);
            toast.error('Erro ao exportar Excel');
        } finally {
            setExporting(false);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

    return (
        <Grid container spacing={2} sx={{ p: 3 }}>
            <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={1}>
                    <IconButton onClick={() => navigate('/plants')}><ArrowBackIcon /></IconButton>
                    <Typography variant="h4">📜 Histórico de Plantas</Typography>
                </Box>
                <Box display="flex" gap={1}>
                    <Button
                        variant="outlined"
                        startIcon={exporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
                        onClick={handleExport}
                        disabled={exporting || plants.length === 0}
                    >
                        Exportar Excel
                    </Button>
                    <Button variant="contained" onClick={() => navigate('/plants')}>Plantas Ativas</Button>
                </Box>
            </Grid>
            {plants.length === 0 && (
                <Grid item xs={12}><Typography color="textSecondary">Nenhuma planta finalizada ainda.</Typography></Grid>
            )}
            {plants.map((p) => {
                const id = p._id || p.id;
                const harvested = p.endType === 'harvested';
                return (
                    <Grid item xs={12} sm={6} md={4} key={id}>
                        <Paper sx={{ p: 2, border: '2px solid #666' }}>
                            <Typography variant="h6">{p.name}</Typography>
                            <Typography variant="body2" color="textSecondary">{p.strain}</Typography>
                            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip label={STAGE_LABELS[p.growthStage] || p.growthStage} size="small" color="primary" />
                                <Chip label={harvested ? 'Colhida' : 'Finalizada'} size="small" color={harvested ? 'success' : 'default'} />
                            </Box>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {p.history?.durationDays != null ? `⏱️ ${p.history.durationDays} dia(s) de vida` : '—'}
                            </Typography>
                            {p.history?.startDate && (
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Início: {new Date(p.history.startDate).toLocaleDateString('pt-BR')}
                                </Typography>
                            )}
                            {p.history?.endDate && (
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Fim: {new Date(p.history.endDate).toLocaleDateString('pt-BR')}
                                </Typography>
                            )}
                            {harvested && p.wetWeight != null && (
                                <Typography variant="caption" color="textSecondary" display="block">💧 Peso úmido: {p.wetWeight}g</Typography>
                            )}
                            {harvested && p.dryWeight != null && (
                                <Typography variant="caption" color="textSecondary" display="block">🌿 Peso seco: {p.dryWeight}g</Typography>
                            )}
                            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                <Tooltip title="Remover do histórico">
                                    <IconButton size="small" color="error" onClick={() => handleDelete(id)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Paper>
                    </Grid>
                );
            })}
        </Grid>
    );
}
